import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import EventTile from "./EventTile";
import logo from "./logo.png";
import "./UserInterface.css"; // You can rename or create a separate CSS if needed

const UserInterface = () => {
  const { orgid } = useParams(); // /user/:orgid
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const apiBase =
    "https://241hbdvql1.execute-api.ap-south-1.amazonaws.com/praud/orgs";

  const fetchEvents = useCallback(async () => {
    if (!orgid) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiBase}/${orgid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        throw new Error(`Failed to load events (${response.status})`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("Events data is not an array");
      }

      setEvents(data);
    } catch (e) {
      console.error("Fetch error:", e);

      if (e.name === "AbortError") {
        setError("Request timed out. Please check your connection and try again.");
      } else if (e.message.includes("Failed to fetch") || e.message.includes("NetworkError")) {
        setError("Network error. Please check your internet connection.");
      } else {
        setError("Unable to load events. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [orgid]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents, retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="status-message">
          <div className="loading-content">
            <div className="loading-spinner large"></div>
            <p>Loading amazing events... 🎪</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="status-message error">
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again 🔄
          </button>
        </div>
      );
    }

    if (events.length === 0) {
      return (
        <div className="status-message">
          <p>No events available right now. Check back soon! 🎭</p>
        </div>
      );
    }

    return (
      <div className="tiles-container">
        {events.map((event, index) => (
          <EventTile key={event.tileID || event.id || index} event={event} />
        ))}
      </div>
    );
  };

  return (
    <div className="UserInterface">
      <header className="header">
        <div className="header-content">
          <img src={logo} alt="Company Logo" className="logo" />
        </div>
      </header>

      <main>{renderContent()}</main>

      <footer className="footer">
        <p>&copy; 2025 Event Hub. Find your next adventure.</p>
      </footer>
    </div>
  );
};

export default UserInterface;
